/**
 * Pipeline analytics — `quoflow.quoflow.quoflow.api.analytics.get_pipeline_analytics` (Task 11).
 * Live shape verified 2026-08-04:
 *
 *   GET analytics.get_pipeline_analytics?from_date=&to_date=
 *     → {by_stage: {...9 statuses...}, conversion_rate, win_rate, avg_cycle_days,
 *        total_enquiries, blockers: [{id, customer, reason, age_days, owner}],
 *        per_purchaser: [{name, handled, won, win_rate}]}
 *
 * Every number here is a backend aggregate (CLAUDE.md's FE/BE split, contract §12) — this file
 * only reshapes the wire, it never re-derives a rate or an average from raw rows on the client.
 * `stage_avg_days` has no source on this wire (per-transition averages need a status-history
 * table nothing here keeps) and is always `{}` — same STUB the old `/api/v1` route left it as.
 * `PurchaserStat.avg_response_days` is likewise absent from the new shape and simply omitted,
 * rather than defaulted to `0` — a real "0 days" would read as suspiciously fast service instead
 * of "unknown", which is the honest state (`PurchaserStat`'s own comment in `../types`).
 */

import { useQuery } from '@tanstack/react-query'
import { callGet } from './bosun'
import { num, str } from './wire'
import { qk, type PipelineRange } from './keys'
import type { Blocker, PipelineAnalytics, PurchaserStat } from '../types'

export interface WireBlocker {
  id: string
  customer: string
  reason: string
  age_days: number
  owner: string
}

export interface WirePurchaserStat {
  name: string
  handled: number
  won: number
  win_rate: number
}

export interface WirePipelineAnalytics {
  by_stage: Record<string, number>
  conversion_rate: number
  win_rate: number
  avg_cycle_days: number
  total_enquiries: number
  blockers: WireBlocker[]
  per_purchaser: WirePurchaserStat[]
}

function toBlocker(w: WireBlocker): Blocker {
  return {
    id: str(w.id),
    customer: str(w.customer),
    reason: str(w.reason),
    age_days: num(w.age_days),
    owner: str(w.owner),
  }
}

function toPurchaserStat(w: WirePurchaserStat): PurchaserStat {
  return {
    name: str(w.name),
    handled: num(w.handled),
    won: num(w.won),
    win_rate: num(w.win_rate),
    // no avg_response_days on this wire — see module header
  }
}

export function toPipelineAnalytics(w: WirePipelineAnalytics): PipelineAnalytics {
  const by_stage: Record<string, number> = {}
  for (const [stage, count] of Object.entries(w.by_stage ?? {})) by_stage[stage] = num(count)

  return {
    by_stage,
    conversion_rate: num(w.conversion_rate),
    win_rate: num(w.win_rate),
    avg_cycle_days: num(w.avg_cycle_days),
    total_enquiries: num(w.total_enquiries),
    stage_avg_days: {}, // STUB — see module header
    blockers: (w.blockers ?? []).map(toBlocker),
    per_purchaser: (w.per_purchaser ?? []).map(toPurchaserStat),
  }
}

export function usePipelineAnalytics(range: PipelineRange = {}) {
  return useQuery({
    queryKey: qk.analyticsPipeline(range),
    queryFn: async () =>
      toPipelineAnalytics(
        await callGet<WirePipelineAnalytics>('analytics', 'get_pipeline_analytics', {
          from_date: range.from_date,
          to_date: range.to_date,
        }),
      ),
  })
}
